theme.submitCartAdd = async function (component, buttons, formData) {
  if (theme.cartAddBusy || component.cartAddPending || buttons.some(button => button.disabled)) return;
  theme.cartAddBusy = component.cartAddPending = true;
  const QuickView = customElements.get('quick-view');
  if (QuickView) QuickView.cached = null;
  const states = buttons.map(button => ({ button, disabled: button.disabled }));
  const previous = theme.cartAddRecovery;
  const fields = new FormData();
  for (const [key, value] of formData.entries()) fields.append(key, value);
  const intent = previous || { variant: String(fields.get('id')), confirmed: false, emitted: false };
  if (intent.feedback) intent.feedback.hidden = true;
  let feedback = component.querySelector('[data-cart-add-error]');
  const needsSync = value => {
    for (const id of ['Cart', 'Cart-Drawer']) {
      const host = document.getElementById(id);
      (host?.cbmCartController || host?.cbmCartDrawerController)?.setNeedsSync(value);
      const checkout = host?.querySelector('.proceed-to-checkout, .button.checkout');
      if (checkout) checkout.inert = value;
    }
  };
  const show = (message, confirmed = false, review = false) => {
    if (!feedback) {
      feedback = document.createElement('p');
      feedback.className = 'product-form__error-message';
      feedback.setAttribute('data-cart-add-error', '');
    }
    if (!feedback.isConnected) (component.isConnected ? component : document.getElementById('Cart-Drawer'))?.appendChild(feedback);
    feedback.setAttribute('role', confirmed ? 'status' : 'alert');
    const title = intent.product?.title || intent.product?.product_title || '';
    feedback.replaceChildren(document.createTextNode((previous ? theme.strings.cartAddPrevious + ' ' : '') + message + (title ? ' ' + title + '.' : '') + ' '));
    const link = document.createElement('a');
    link.href = theme.routes.cart_url;
    link.textContent = theme.strings.viewCart;
    feedback.appendChild(link);
    if (review) {
      const proceed = document.createElement('button');
      proceed.type = 'button';
      proceed.className = 'button';
      proceed.setAttribute('data-cart-add-continue', '');
      proceed.textContent = theme.strings.cartAddContinue;
      proceed.addEventListener('click', () => {
        if (theme.cartAddBusy || theme.cartAddRecovery !== intent) return;
        // Explicit acknowledgement permits a new action; it never replays this request.
        theme.cartAddRecovery = null;
        feedback.hidden = true;
        proceed.remove();
        const target = buttons.find(button => button.isConnected && !button.disabled) || document.getElementById('cart-drawer-toggle');
        target?.focus({ preventScroll: true });
      });
      feedback.appendChild(document.createTextNode(' '));
      feedback.appendChild(proceed);
    }
    feedback.hidden = false;
    intent.feedback = feedback;
  };
  const request = async (url, options) => {
    const parseResponse = async response => {
      const state = await response.json();
      if (!response.ok || !state || state.status || state.errors) {
        const error = new Error('cart-add-response-error');
        error.cartMessage = typeof state?.description === 'string' ? state.description : '';
        error.cartResponse = state;
        throw error;
      }
      return state;
    };
    if (options?.method === 'POST') {
      // Slow feedback is not a failed request. Await the original body without a write deadline.
      const slowNotice = setTimeout(() => show(theme.strings.cartAddSlow, true), 8000);
      try { return await fetch(url, options).then(parseResponse); }
      finally { clearTimeout(slowNotice); }
    }
    const controller = new AbortController();
    let timer;
    try {
      return await Promise.race([
        Promise.resolve().then(() => fetch(url, { ...options, signal: controller.signal })).then(parseResponse),
        new Promise((resolve, reject) => {
          timer = setTimeout(() => { controller.abort(); reject(new Error('cart-read-timeout')); }, 8000);
        })
      ]);
    } finally { clearTimeout(timer); }
  };
  const emitConfirmed = () => {
    if (intent.confirmed && !intent.emitted) {
      intent.emitted = true;
      dispatchCustomEvent('cart:item-added', { product: intent.product });
    }
  };
  try {
    component.handleErrorMessage?.();
    buttons.forEach(button => {
      button.disabled = true;
      button.classList.add('loading');
      button.setAttribute('aria-disabled', 'true');
    });
    let state;
    if (!previous) {
      theme.cartAddRecovery = intent;
      try {
        state = await request(theme.routes.cart_add_url, {
          method: 'POST', headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' }, body: fields
        });
        const items = Array.isArray(state.items) ? state.items : [state];
        const added = items.find(item => String(item.variant_id || item.id) === intent.variant && Number(item.quantity) > 0);
        if (added) { intent.confirmed = true; intent.product = added; }
      } catch (error) {
        intent.errorMessage = error.cartMessage;
        if (error.cartResponse && component.tagName === 'PRODUCT-FORM') {
          dispatchCustomEvent('product:variant-error', {
            source: 'product-form', productVariantId: intent.variant,
            errors: error.cartMessage || theme.strings.cartAddError, message: error.cartResponse.message
          });
        }
      }
    }
    if (state && intent.confirmed) {
      try {
        // The entry point uses the shared atomic section validator/renderer.
        component.renderContents(state);
        theme.cartAddRecovery = null;
        needsSync(false);
        if (feedback) feedback.hidden = true;
        emitConfirmed();
        return state;
      } catch { /* A rendering error does not undo a confirmed write. */ }
    }
    needsSync(true);
    show(intent.confirmed ? theme.strings.cartAddViewError : theme.strings.cartAddChecking, intent.confirmed);
    let current;
    if (!intent.confirmed) {
      try {
        current = await request(theme.routes.cart_url.replace(/\.js$/, '') + '.js', { cache: 'no-store' });
        if (!Array.isArray(current.items) || !Number.isInteger(current.item_count)) current = null;
      } catch { /* A later click can read again; never replay an uncertain write. */ }
    }
    let rendered = false;
    try {
      // Read HTML after the cart snapshot, so it cannot be an earlier parallel response.
      const sections = component.getSectionsToRender().map(section => section.section).join(',');
      const latest = await request(theme.routes.cart_url + '?sections=' + sections, { cache: 'no-store' });
      rendered = window.CBMCartUx.replaceSections(document, latest, DOMParser);
    } catch { /* Keep a direct cart link available when presentation cannot recover. */ }
    const reviewReady = Boolean(rendered && (intent.confirmed || current));
    needsSync(!reviewReady);
    if (intent.confirmed) {
      if (rendered) theme.cartAddRecovery = null;
      show(rendered ? theme.strings.cartAddConfirmed : theme.strings.cartAddViewError, true);
      emitConfirmed();
      return state || null;
    }
    // A current snapshot is not evidence of what this earlier request added.
    show((intent.errorMessage ? intent.errorMessage + ' ' : '') +
      (reviewReady ? theme.strings.cartAddReview : theme.strings.cartAddError), false, reviewReady);
    return null;
  } finally {
    states.forEach(({ button, disabled }) => {
      const selector = component.tagName === 'PRODUCT-FORM' && component.closest('.thb-product-detail')?.querySelector('variant-selects');
      button.disabled = disabled || Boolean(selector && (!selector.currentVariant?.available || selector.productWrapper?.cbmVariantPending));
      button.classList.remove('loading');
      button.removeAttribute('aria-disabled');
    });
    theme.cartAddBusy = component.cartAddPending = false;
  }
};

if (typeof debounce === 'undefined') {
  function debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}
var dispatchCustomEvent = function dispatchCustomEvent(eventName) {
  var data = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var detail = {
    detail: data
  };
  var event = new CustomEvent(eventName, data ? detail : null);
  document.dispatchEvent(event);
};

/**
 *  @class
 *  @function Quantity
 */
if (!customElements.get('quantity-selector')) {
  class QuantityInput extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('.qty');
      this.step = this.input.getAttribute('step');
      this.changeEvent = new Event('change', {
        bubbles: true
      });
      // Create buttons
      this.subtract = this.querySelector('.minus');
      this.add = this.querySelector('.plus');

      // Add functionality to buttons
      this.subtract.addEventListener('click', () => this.change_quantity(-1 * this.step));
      this.add.addEventListener('click', () => this.change_quantity(1 * this.step));

      this.validateQtyRules();
    }
    connectedCallback() {
      this.classList.add('buttons_added');
    }
    change_quantity(change) {
      // Get current value
      let quantity = Number(this.input.value);

      // Ensure quantity is a valid number
      if (isNaN(quantity)) quantity = 1;

      // Check for min & max
      if (this.input.getAttribute('min') > (quantity + change)) {
        return;
      }
      if (this.input.getAttribute('max')) {
        if (this.input.getAttribute('max') < (quantity + change)) {
          return;
        }
      }

      // Change quantity
      quantity += change;

      // Ensure quantity is always a number
      quantity = Math.max(quantity, 1);

      // Output number
      this.input.value = quantity;

      this.input.dispatchEvent(this.changeEvent);

      this.validateQtyRules();
    }
    validateQtyRules() {
      const value = parseInt(this.input.value);
      if (this.input.min) {
        const min = parseInt(this.input.min);
        this.subtract.classList.toggle('disabled', value <= min);
      }
      if (this.input.max) {
        const max = parseInt(this.input.max);
        this.add.classList.toggle('disabled', value >= max);
      }
    }
  }
  customElements.define('quantity-selector', QuantityInput);
}

/**
 *  @class
 *  @function ArrowSubMenu
 */
class ArrowSubMenu {

  constructor(self) {
    this.submenu = self.parentNode.querySelector('.sub-menu');
    this.arrow = self;
    // Add functionality to buttons
    self.addEventListener('click', (e) => this.toggle_submenu(e));
  }

  toggle_submenu(e) {
    e.preventDefault();
    let submenu = this.submenu;

    if (!submenu.classList.contains('active')) {
      submenu.classList.add('active');

    } else {
      submenu.classList.remove('active');
      this.arrow.blur();
    }
  }
}
let arrows = document.querySelectorAll('.thb-arrow');
arrows.forEach((arrow) => {
  new ArrowSubMenu(arrow);
});

/**
 *  @class
 *  @function ProductCard
 */
if (!customElements.get('product-card')) {
  class ProductCard extends HTMLElement {
    constructor() {
      super();
      this.swatches = this.querySelector('.product-card-swatches');
      this.image = this.querySelector('.product-featured-image-link .product-primary-image');
      this.additional_images = this.querySelectorAll('.product-secondary-image');
      this.additional_images_nav = this.querySelectorAll('.product-secondary-images-nav li');
      this.quick_add = this.querySelector('.product-card--add-to-cart-button-simple');
    }
    connectedCallback() {
      if (this.swatches) {
        this.enableSwatches(this.swatches, this.image);
      }
      if (this.additional_images) {
        this.enableAdditionalImages();
      }
      if (this.quick_add) {
        this.enableQuickAdd();
      }
    }
    enableAdditionalImages() {
      let image_length = this.additional_images.length;
      let images = this.additional_images;
      let nav = this.additional_images_nav;
      let image_container = this.querySelector('.product-featured-image');
      const mousemove = function (e) {
        let l = e.offsetX;
        let w = this.getBoundingClientRect().width;
        let prc = l / w;
        let sel = Math.floor(prc * image_length);
        let selimg = images[sel];
        images.forEach((image, index) => {
          if (image.classList.contains('hover')) {
            image.classList.remove('hover');
            if (nav.length) {
              nav[index].classList.remove('active');
            }
          }
        });
        if (selimg) {
          if (!selimg.classList.contains('hover')) {
            selimg.classList.add('hover');
            if (nav.length) {
              nav[sel].classList.add('active');
            }
          }
        }

      };
      const mouseleave = function (e) {
        images.forEach((image, index) => {
          image.classList.remove('hover');
          if (nav.length) {
            nav[index].classList.remove('active');
          }
        });
      };
      if (image_container) {
        image_container.addEventListener('touchstart', mousemove, {
          passive: true
        });
        image_container.addEventListener('touchmove', mousemove, {
          passive: true
        });
        image_container.addEventListener('touchend', mouseleave, {
          passive: true
        });
        image_container.addEventListener('mouseenter', mousemove, {
          passive: true
        });
        image_container.addEventListener('mousemove', mousemove, {
          passive: true
        });
        image_container.addEventListener('mouseleave', mouseleave, {
          passive: true
        });
      }

      images.forEach(function (image) {
        window.addEventListener('load', (event) => {
          lazySizes.loader.unveil(image);
        });
      });
    }
    enableSwatches(swatches, image) {
      let swatch_list = swatches.querySelectorAll('.product-card-swatch'),
        org_srcset = image ? image.dataset.srcset : '';

      swatch_list.forEach((swatch, index) => {
        window.addEventListener('load', (event) => {
          let image = new Image();
          image.srcset = swatch.dataset.srcset;
          lazySizes.loader.unveil(image);
        });
        swatch.addEventListener('mouseover', function () {

          [].forEach.call(swatch_list, function (el) {
            el.classList.remove('active');
          });
          if (image) {
            if (swatch.dataset.srcset) {
              image.setAttribute('srcset', swatch.dataset.srcset);
            } else {
              image.setAttribute('srcset', org_srcset);
            }
          }

          swatch.classList.add('active');
        });
        swatch.addEventListener('click', function (evt) {
          window.location.href = this.dataset.href;
          evt.preventDefault();
        });
      });
    }
    enableQuickAdd() {
      this.quick_add.tabIndex = 0;
      this.quick_add.addEventListener('click', this.quickAdd.bind(this));
    }

    quickAdd(evt) {
      evt.preventDefault();
      if (this.cartAddPending || this.quick_add.disabled) return;
      const formData = new FormData(this.form);
      formData.set('id', this.quick_add.dataset.productId);
      formData.set('quantity', 1);
      formData.append('sections', this.getSectionsToRender().map(section => section.section));
      formData.append('sections_url', window.location.pathname);
      return theme.submitCartAdd(this, [this.quick_add], formData);
    }
    getSectionsToRender() {
      return [{
        id: 'Cart',
        section: 'main-cart',
        selector: '.thb-cart-form'
      },
      {
        id: 'Cart-Drawer',
        section: 'cart-drawer',
        selector: '.cart-drawer'
      },
      {
        id: 'cart-drawer-toggle',
        section: 'cart-bubble',
        selector: '.thb-item-count'
      }];
    }
    renderContents(parsedState) {
      if (!window.CBMCartUx.replaceSections(document, parsedState.sections, DOMParser)) throw new Error('cart-sections-missing');

    }
    getSectionInnerHTML(html, selector = '.shopify-section') {
      return new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(selector).innerHTML;
    }
  }
  customElements.define('product-card', ProductCard);
}

/**
 *  @class
 *  @function ProductCardSmall
 */
if (!customElements.get('product-card-small')) {
  class ProductCardSmall extends HTMLElement {
    constructor() {
      super();

      this.quick_add_enabled = this.classList.contains('quick-add-to-card--true');
      this.button = this.querySelector('button');
      this.id = this.dataset.id;
      this.url = this.dataset.url;
    }
    connectedCallback() {
      if (this.quick_add_enabled) {
        this.setEventListeners();
      }
    }
    setEventListeners() {
      this.button.addEventListener('click', this.addToCart.bind(this));
    }
    getSectionsToRender() {
      return [{
        id: 'Cart',
        section: 'main-cart',
        selector: '.thb-cart-form'
      },
      {
        id: 'Cart-Drawer',
        section: 'cart-drawer',
        selector: '.cart-drawer'
      },
      {
        id: 'cart-drawer-toggle',
        section: 'cart-bubble',
        selector: '.thb-item-count'
      }];
    }

    addToCart() {
      if (this.cartAddPending || this.button.disabled) return;
      const formData = new FormData();
      formData.append('id', this.id);
      formData.append('quantity', 1);
      formData.append('sections', this.getSectionsToRender().map(section => section.section));
      formData.append('sections_url', window.location.pathname);
      return theme.submitCartAdd(this, [this.button], formData);
    }
    renderContents(parsedState) {
      if (!window.CBMCartUx.replaceSections(document, parsedState.sections, DOMParser)) throw new Error('cart-sections-missing');
    }
    getSectionInnerHTML(html, selector = '.shopify-section') {
      return new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(selector).innerHTML;
    }
  }
  customElements.define('product-card-small', ProductCardSmall);
}

/**
 *  @class
 *  @function PanelClose
 */
class PanelClose extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', event => this.close_panel(event));
    this.addEventListener('keydown', event => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      if (event.key === 'Enter' && !event.repeat) this.click();
    });
    this.addEventListener('keyup', event => {
      if (event.key !== ' ') return;
      event.preventDefault();
      this.click();
    });
    if (!PanelClose.listening) {
      PanelClose.listening = true;
      document.addEventListener('keyup', event => {
        if (event.cbmOverlayHandled || event.code?.toUpperCase() !== 'ESCAPE') return;
        const QuickView = customElements.get('quick-view');
        if (document.body.classList.contains('open-cc--product') || QuickView?.request) {
          event.cbmOverlayHandled = true;
          event.preventDefault();
          QuickView?.close();
          return;
        }
        const panel = document.activeElement?.closest('.side-panel.active') ||
          Array.from(document.querySelectorAll('.side-panel.active')).pop();
        if (panel) { event.cbmOverlayHandled = true; event.preventDefault(); PanelClose.closePanel(panel); }
      });
      document.querySelector('.click-capture')?.addEventListener('click', () => {
        const panel = Array.from(document.querySelectorAll('.side-panel.active')).pop();
        if (panel) PanelClose.closePanel(panel);
      });
    }
  }
  connectedCallback() {
    this.setAttribute('role', 'button');
    this.tabIndex = 0;
    const panel = this.closest('.side-panel');
    if (panel && !panel.cbmPanelStateObserver) {
      panel.cbmPanelStateObserver = new MutationObserver(() => PanelClose.syncPanelState(panel, true));
      panel.cbmPanelStateObserver.observe(panel, { attributes: true, attributeFilter: ['class'] });
    }
    if (panel) PanelClose.syncPanelState(panel);
  }
  static syncPanelState(panel, moveFocus = false) {
    const open = panel.classList.contains('active');
    const returnFocus = !open && panel.contains(document.activeElement) ? panel.cbmReturnFocus : null;
    PanelClose.openPanels = (PanelClose.openPanels || []).filter(item => item.isConnected && item.classList.contains('active'));
    if (open && !PanelClose.openPanels.includes(panel)) PanelClose.openPanels.push(panel);
    const top = PanelClose.openPanels[PanelClose.openPanels.length - 1];
    document.querySelectorAll('.side-panel').forEach(item => {
      item.inert = item !== top;
      item.setAttribute('aria-hidden', item === top ? 'false' : 'true');
    });
    if (returnFocus?.isConnected) returnFocus.focus({ preventScroll: true });
    if (moveFocus && open && panel === top && !panel.contains(document.activeElement)) {
      panel.querySelector('side-panel-close')?.focus({ preventScroll: true });
    }
  }
  static syncScrollLock() {
    document.body.classList.toggle('overflow-hidden', Boolean(
      document.querySelector('.mobile-toggle-wrapper.active, .side-panel.active')));
  }
  static closePanel(panel) {
    if (!panel) return;
    if (panel.id === 'Product-Drawer') {
      customElements.get('quick-view')?.close();
      return;
    }
    panel.classList.remove('active');
    PanelClose.syncPanelState(panel);
    document.body.classList.toggle('open-cc', Boolean(document.querySelector('.side-panel.active:not(#Product-Drawer)')));
    PanelClose.syncScrollLock();
    const trigger = panel.cbmReturnFocus;
    if (trigger?.isConnected) trigger.focus({ preventScroll: true });
  }
  close_panel(event) {
    event.preventDefault();
    PanelClose.closePanel(this.closest('.side-panel'));
  }
}
customElements.define('side-panel-close', PanelClose);

/**
 *  @class
 *  @function CartDrawer
 */
class CartDrawer {

  constructor() {
    this.container = document.getElementById('Cart-Drawer');

    if (!this.container) {
      return;
    }
    if (this.container.cbmCartDrawerController) {
      this.container.cbmCartDrawerController.removeProductEvent();
      this.container.cbmCartDrawerController.notesToggle();
      this.container.cbmCartDrawerController.termsCheckbox();
      return this.container.cbmCartDrawerController;
    }
    this.container.cbmCartDrawerController = this;
    let button = document.getElementById('cart-drawer-toggle');


    // Add functionality to buttons
    button?.addEventListener('click', (e) => {
      e.preventDefault();
      this.container.cbmReturnFocus = button;
      document.body.classList.add('open-cc');
      this.container.classList.add('active');
      PanelClose.syncPanelState(this.container);
      this.container.focus();
      dispatchCustomEvent('cart-drawer:open');
    });

    this.debouncedOnChange = window.CBMCartUx.quantityChangeHandler(this);

    document.addEventListener('cart:refresh', (event) => {
      if (!document.getElementById('Cart')) this.refresh();
    });

    this.container.addEventListener('change', this.debouncedOnChange.bind(this));

    this.notesToggle();
    this.removeProductEvent();
    // Terms checkbox
    this.termsCheckbox();
  }
  onChange(event) {
    if (event.target.classList.contains('qty')) {
      this.updateQuantity(event.target.dataset.index, event.target.value);
    }
  }
  removeProductEvent() {
    let removes = this.container.querySelectorAll('.remove');

    removes.forEach((remove) => {
      if (remove.cbmRemoveHandler) remove.removeEventListener('click', remove.cbmRemoveHandler);
      remove.cbmRemoveHandler = event => {
        event.preventDefault();
        this.updateQuantity(event.currentTarget.dataset.index, '0');
      };
      remove.addEventListener('click', remove.cbmRemoveHandler);
    });
  }
  getSectionsToRender() {
    return [{
      id: 'Cart',
      section: 'main-cart',
      selector: '.thb-cart-form'
    }, {
      id: 'Cart-Drawer',
      section: 'cart-drawer',
      selector: '.cart-drawer'
    },
    {
      id: 'cart-drawer-toggle',
      section: 'cart-bubble',
      selector: '.thb-item-count'
    }].filter(section => section.id !== 'Cart' || document.getElementById('Cart'));
  }
  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }
  termsCheckbox() {
    let terms_checkbox = this.container.querySelector('#CartDrawerTerms'),
      checkout_button = this.container.querySelector('.button.checkout');

    if (terms_checkbox && checkout_button) {
      terms_checkbox.setCustomValidity(theme.strings.requiresTerms);
      if (checkout_button.cbmTermsHandler) checkout_button.removeEventListener('click', checkout_button.cbmTermsHandler);
      checkout_button.cbmTermsHandler = function (e) {
        if (!terms_checkbox.checked) {
          terms_checkbox.reportValidity();
          terms_checkbox.focus();
          e.preventDefault();
        }
      };
      checkout_button.addEventListener('click', checkout_button.cbmTermsHandler);
    }
  }
  notesToggle() {
    let notes_toggle = document.getElementById('order-note-toggle');

    if (!notes_toggle) {
      return;
    }

    if (notes_toggle.cbmNotesBound) return;
    notes_toggle.cbmNotesBound = true;
    notes_toggle.addEventListener('click', (event) => {
      notes_toggle.nextElementSibling.classList.add('active');
    });
    notes_toggle.nextElementSibling.querySelectorAll('.button, .order-note-toggle__content-overlay').forEach((el) => {
      el.addEventListener('click', (event) => {
        notes_toggle.nextElementSibling.classList.remove('active');
        this.saveNotes();
      });
    });
  }
  saveNotes() {
    fetch(`${theme.routes.cart_update_url}.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': `application/json`
      },
      body: JSON.stringify({
        'note': document.getElementById('mini-cart__notes').value
      })
    });
  }
  updateQuantity(line, quantity) {
    const item = this.container.querySelector(`#CartDrawerItem-${line}`);
    const itemKey = item?.querySelector('quantity-selector')?.dataset.id;
    const version = this.cbmQueueVersion || 0;
    const operation = (this.cbmCartMutationQueue || Promise.resolve()).then(async () => {
      if (version !== (this.cbmQueueVersion || 0)) return null;
      if (this.needsSync) { await this.refreshNow(); return null; }
      return this.updateQuantityNow(line, quantity, itemKey);
    });
    this.cbmCartMutationQueue = operation.catch(() => null);
    return operation;
  }
  async updateQuantityNow(line, quantity, itemKey) {
    const selector = itemKey && Array.from(this.container.querySelectorAll('quantity-selector[data-id]'))
      .find(element => element.dataset.id === itemKey);
    const item = itemKey ? selector?.closest('.product-cart-item') : this.container.querySelector(`#CartDrawerItem-${line}`);
    if (!item) return null;
    item.classList.add('thb-loading');
    this.container.setAttribute('aria-busy', 'true');
    this.container.querySelector('[data-cart-drawer-error]')?.remove();
    const change = {
      quantity: Number(quantity),
      sections: this.getSectionsToRender().map(section => section.section),
      sections_url: window.location.pathname
    };
    if (itemKey) change.id = itemKey;
    else change.line = Number(line);
    dispatchCustomEvent('line-item:change:start', { quantity });
    try {
      const state = await window.CBMCartUx.requestJSON(window, theme.routes.cart_change_url, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(change)
      }, 8000);
      this.renderState(state.sections);
      dispatchCustomEvent('line-item:change:end', { quantity, cart: state });
      return state;
    } catch (error) {
      this.cbmQueueVersion = (this.cbmQueueVersion || 0) + 1;
      this.setNeedsSync(true);
      this.container.querySelectorAll('input.qty').forEach(input => { input.value = input.defaultValue; });
      try { await this.readCurrentSections(); } catch (syncError) { this.setNeedsSync(true); }
      this.showRecoveryError();
      dispatchCustomEvent('line-item:change:error', { quantity, error });
      return null;
    } finally {
      item.classList.remove('thb-loading');
      this.container.querySelectorAll('.thb-loading').forEach(row => row.classList.remove('thb-loading'));
      this.container.removeAttribute('aria-busy');
    }
  }
  setNeedsSync(value) {
    this.needsSync = value;
    const checkout = this.container.querySelector('.button.checkout');
    if (checkout) checkout.inert = value;
    const other = document.getElementById('Cart');
    const controller = other?.cbmCartController || other?.cbmCartDrawerController;
    if (controller) controller.needsSync = value;
    const otherCheckout = other?.querySelector('.proceed-to-checkout');
    if (otherCheckout) otherCheckout.inert = value;
  }
  renderState(sections) {
    if (!window.CBMCartUx.replaceSections(document, sections, DOMParser)) throw new Error('cart-sections-missing');
    this.removeProductEvent();
    this.notesToggle();
    this.termsCheckbox();
    this.setNeedsSync(false);
  }
  async readCurrentSections() {
    const sections = this.getSectionsToRender().map(section => section.section).join(',');
    const state = await window.CBMCartUx.requestJSON(window, `${theme.routes.cart_url}?sections=${sections}`, {}, 8000);
    this.renderState(state);
    return state;
  }
  showRecoveryError() {
    this.container.querySelector('[data-cart-drawer-error]')?.remove();
    const feedback = document.createElement('p');
    feedback.setAttribute('data-cart-drawer-error', '');
    feedback.setAttribute('role', 'alert');
    feedback.append(this.needsSync ? 'Unable to confirm your cart. Refresh it before continuing. ' : 'The update could not be confirmed. Your current cart is shown. ');
    const retry = document.createElement('button');
    retry.type = 'button';
    retry.textContent = 'Refresh cart';
    retry.addEventListener('click', () => this.refresh());
    feedback.append(retry);
    (this.container.querySelector('.cart-drawer') || this.container).prepend(feedback);
  }
  refresh() {
    const operation = (this.cbmCartMutationQueue || Promise.resolve()).then(() => this.refreshNow());
    this.cbmCartMutationQueue = operation.catch(() => null);
    return operation;
  }
  async refreshNow() {
    this.container.setAttribute('aria-busy', 'true');
    try { return await this.readCurrentSections(); }
    catch (error) { this.setNeedsSync(true); this.showRecoveryError(); return null; }
    finally { this.container.removeAttribute('aria-busy'); }
  }
}

/**
 *  @class
 *  @function FooterMenuToggle
 */
class FooterMenuToggle {
  constructor() {
    let _this = this;
    // resize on initial load
    document.querySelectorAll(".thb-widget-title.collapsible").forEach((button) => {
      button.addEventListener("click", (e) => {
        button.classList.toggle('active');
      });
    });
  }
}

/**
 *  @class
 *  @function CustomSelect
 */
if (!customElements.get('custom-select')) {
  class CustomSelect extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      if (this.cbmInitialized) return;
      const initialize = () => {
        if (!this.isConnected || this.cbmInitialized) return;
        if (!this.querySelector('input') || !this.querySelector('.custom-select--button .custom-select--text') || !this.querySelector('.custom-select--list button')) return;
        this.cbmInitObserver?.disconnect();
        this.initializeSelect();
        this.cbmInitialized = true;
      };
      this.cbmInitObserver = new MutationObserver(initialize);
      this.cbmInitObserver.observe(this, { childList: true, subtree: true });
      queueMicrotask(initialize);
    }
    disconnectedCallback() {
      this.cbmInitObserver?.disconnect();
    }
    initializeSelect() {
      this.detailsements = {
        form: this.closest('form'),
        input: this.querySelector('input'),
        button: this.querySelector('.custom-select--button'),
        panel: this.querySelector('.custom-select--list'),
      };
      this.autoClose = this.dataset.autoClose;
      this.detailsements.button.addEventListener('click', this.toggleSelector.bind(this));
      this.detailsements.button.addEventListener('focusout', this.closeSelector.bind(this));
      this.addEventListener('keyup', this.onContainerKeyUp.bind(this));

      this.detailsements.panel.querySelectorAll('button').forEach(item => item.addEventListener('click', this.onItemClick.bind(this)));
    }

    hidePanel() {
      this.detailsements.button.setAttribute('aria-expanded', 'false');
      this.classList.remove('custom-select--active');
    }

    onContainerKeyUp(event) {
      if (event.code.toUpperCase() !== 'ESCAPE') return;

      this.hidePanel();
      this.detailsements.button.focus();
    }

    onItemClick(event) {
      event.preventDefault();
      this.detailsements.input.value = event.currentTarget.dataset.value;
      this.detailsements.input.dispatchEvent(new Event('change'));
      this.detailsements.button.querySelector('.custom-select--text').textContent = event.currentTarget.textContent;
      if (this.detailsements.form) {
        this.detailsements.form.dispatchEvent(new Event('input'));
      }
      if (this.autoClose) {
        this.classList.remove('custom-select--active');
      }
    }

    toggleSelector(event) {
      event.preventDefault();
      this.classList.toggle('custom-select--active');
      if (this.classList.contains('custom-select--active')) {
        this.detailsements.button.focus();
        this.detailsements.button.setAttribute('aria-expanded', (this.detailsements.button.getAttribute('aria-expanded') === 'false').toString());
      } else {
        this.hidePanel();
      }
    }

    closeSelector(event) {
      const shouldClose = event.relatedTarget && event.relatedTarget.classList.contains('custom-select--button');
      if (event.relatedTarget === null || shouldClose) {
        this.hidePanel();
      }
    }
  }
  customElements.define('custom-select', CustomSelect);
}
/**
 *  @class
 *  @function LocalizationForm
 */
if (!customElements.get('localization-form')) {
  class LocalizationForm extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      if (this.cbmInitialized) return;
      const initialize = () => {
        if (!this.isConnected || this.cbmInitialized) return;
        if (!this.querySelector('form input[name="locale_code"], form input[name="country_code"]')) return;
        this.cbmInitObserver?.disconnect();
        this.initializeLocalization();
        this.cbmInitialized = true;
      };
      this.cbmInitObserver = new MutationObserver(initialize);
      this.cbmInitObserver.observe(this, { childList: true, subtree: true });
      queueMicrotask(initialize);
    }
    disconnectedCallback() {
      this.cbmInitObserver?.disconnect();
    }
    initializeLocalization() {
      this.form = this.querySelector('form');
      this.inputs = this.form.querySelectorAll('input[name="locale_code"], input[name="country_code"]');
      this.debouncedOnSubmit = debounce((event) => {
        this.onSubmitHandler(event);
      }, 200);
      this.inputs.forEach(item => item.addEventListener('change', this.debouncedOnSubmit.bind(this)));
    }
    onSubmitHandler(event) {
      if (this.form) this.form.submit();
    }
  }
  customElements.define('localization-form', LocalizationForm);
}

/**
 *  @class
 *  @function SidePanelContentTabs
 */
if (!customElements.get('side-panel-content-tabs')) {
  class SidePanelContentTabs extends HTMLElement {
    constructor() {
      super();
      this.buttons = this.querySelectorAll('button');
      this.panels = this.parentElement.querySelectorAll('.side-panel-content--tab-panel');
    }
    connectedCallback() {
      this.setupButtonObservers();
    }
    disconnectedCallback() {

    }
    setupButtonObservers() {
      this.buttons.forEach((item, i) => {
        item.addEventListener('click', (e) => {
          this.toggleActiveClass(i);
        });
      });
    }
    toggleActiveClass(i) {
      this.buttons.forEach((button) => {
        button.classList.remove('tab-active');
      });
      this.buttons[i].classList.add('tab-active');

      this.panels.forEach((panel) => {
        panel.classList.remove('tab-active');
      });
      this.panels[i].classList.add('tab-active');
    }
  }

  customElements.define('side-panel-content-tabs', SidePanelContentTabs);
}

/**
 *  @class
 *  @function CollapsibleRow
 */
if (!customElements.get('collapsible-row')) {
  // https://css-tricks.com/how-to-animate-the-details-element/
  class CollapsibleRow extends HTMLElement {
    constructor() {
      super();

      this.details = this.querySelector('details');
      this.summary = this.querySelector('summary');
      this.content = this.querySelector('.collapsible__content');

      // Store the animation object (so we can cancel it if needed)
      this.animation = null;
      // Store if the element is closing
      this.isClosing = false;
      // Store if the element is expanding
      this.isExpanding = false;
    }
    connectedCallback() {
      this.setListeners();
    }
    setListeners() {
      this.querySelector('summary').addEventListener('click', (e) => this.onClick(e));
    }
    instantClose() {
      this.tl.timeScale(10).reverse();
    }
    animateClose() {
      this.tl.timeScale(3).reverse();
    }
    animateOpen() {
      this.tl.timeScale(1).play();
    }
    onClick(e) {
      // Stop default behaviour from the browser
      e.preventDefault();
      // Add an overflow on the <details> to avoid content overflowing
      this.details.style.overflow = 'hidden';
      // Check if the element is being closed or is already closed
      if (this.isClosing || !this.details.open) {
        this.open();
        // Check if the element is being openned or is already open
      } else if (this.isExpanding || this.details.open) {
        this.shrink();
      }
    }
    shrink() {
      // Set the element as "being closed"
      this.isClosing = true;

      // Store the current height of the element
      const startHeight = `${this.details.offsetHeight}px`;
      // Calculate the height of the summary
      const endHeight = `${this.summary.offsetHeight}px`;

      // If there is already an animation running
      if (this.animation) {
        // Cancel the current animation
        this.animation.cancel();
      }

      // Start a WAAPI animation
      this.animation = this.details.animate({
        // Set the keyframes from the startHeight to endHeight
        height: [startHeight, endHeight]
      }, {
        duration: 250,
        easing: 'ease'
      });

      // When the animation is complete, call onAnimationFinish()
      this.animation.onfinish = () => this.onAnimationFinish(false);
      // If the animation is cancelled, isClosing variable is set to false
      this.animation.oncancel = () => this.isClosing = false;
    }

    open() {
      // Apply a fixed height on the element
      this.details.style.height = `${this.details.offsetHeight}px`;
      // Force the [open] attribute on the details element
      this.details.open = true;
      // Wait for the next frame to call the expand function
      window.requestAnimationFrame(() => this.expand());
    }

    expand() {
      // Set the element as "being expanding"
      this.isExpanding = true;
      // Get the current fixed height of the element
      const startHeight = `${this.details.offsetHeight}px`;
      // Calculate the open height of the element (summary height + content height)
      const endHeight = `${this.summary.offsetHeight + this.content.offsetHeight}px`;

      // If there is already an animation running
      if (this.animation) {
        // Cancel the current animation
        this.animation.cancel();
      }

      // Start a WAAPI animation
      this.animation = this.details.animate({
        // Set the keyframes from the startHeight to endHeight
        height: [startHeight, endHeight]
      }, {
        duration: 400,
        easing: 'ease-out'
      });
      // When the animation is complete, call onAnimationFinish()
      this.animation.onfinish = () => this.onAnimationFinish(true);
      // If the animation is cancelled, isExpanding variable is set to false
      this.animation.oncancel = () => this.isExpanding = false;
    }

    onAnimationFinish(open) {
      // Set the open attribute based on the parameter
      this.details.open = open;
      // Clear the stored animation
      this.animation = null;
      // Reset isClosing & isExpanding
      this.isClosing = false;
      this.isExpanding = false;
      // Remove the overflow hidden and the fixed height
      this.details.style.height = this.details.style.overflow = '';
    }
  }
  customElements.define('collapsible-row', CollapsibleRow);
}

/**
 *  @class
 *  @function ProductRecommendations
 */
class ProductRecommendations extends HTMLElement {
  constructor() {
    super();

  }
  fetchProducts() {
    fetch(this.dataset.url)
      .then(response => response.text())
      .then(text => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations && recommendations.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        this.classList.add('product-recommendations--loaded');

      })
      .catch(e => {
        console.error(e);
      });
  }
  connectedCallback() {
    this.fetchProducts();
  }
}

customElements.define('product-recommendations', ProductRecommendations);

/**
 *  @class
 *  @function QuickView
 */
if (!customElements.get('quick-view')) {
  class QuickView extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      if (this.quickViewBound) return;
      this.quickViewBound = true;
      if (!this.closest('a')) {
        this.tabIndex = 0;
        this.setAttribute('role', 'button');
        this.addEventListener('keydown', event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.click();
          }
        });
      }
      this.cc = document.querySelector('.click-capture--product');
      this.drawer = document.getElementById('Product-Drawer');
      this.body = document.body;

      this.addEventListener('click', this.setupEventListener.bind(this));
      if (this.cc && !this.cc.quickViewCloseBound) {
        this.cc.quickViewCloseBound = true;
        this.cc.addEventListener('click', () => QuickView.close());
      }

    }
    setupCCEventListener(e) {
      QuickView.close();
    }
    static close() {
      const returnFocus = QuickView.request?.trigger || document.getElementById('Product-Drawer')?.cbmReturnFocus;
      const sequence = QuickView.sequence = (QuickView.sequence || 0) + 1;
      QuickView.request?.controller.abort();
      QuickView.request?.trigger.classList.remove('loading');
      QuickView.request = null;
      clearTimeout(QuickView.closeTimer);
      const drawer = document.getElementById('Product-Drawer');
      document.body.classList.remove('open-cc--product');
      drawer?.classList.remove('active');
      if (drawer) customElements.get('side-panel-close')?.syncPanelState(drawer);
      customElements.get('side-panel-close')?.syncScrollLock();
      if (returnFocus?.isConnected) returnFocus.focus({ preventScroll: true });
      QuickView.closeTimer = setTimeout(() => {
        if (QuickView.sequence === sequence && !drawer?.classList.contains('active')) {
          drawer?.querySelector('#Product-Drawer-Content')?.replaceChildren();
        }
      }, 260);
    }
    waitForStyles(signal) {
      const link = document.getElementById('Product');
      if (!link) return Promise.resolve();
      return new Promise((resolve, reject) => {
        const finish = (error) => {
          link.removeEventListener('load', loaded);
          link.removeEventListener('error', failed);
          signal.removeEventListener('abort', failed);
          if (error) reject(error);
          else resolve();
        };
        const loaded = () => finish();
        const failed = () => finish(new Error('quick-view-style-unavailable'));
        link.addEventListener('load', loaded);
        link.addEventListener('error', failed);
        signal.addEventListener('abort', failed, { once: true });
        const ready = link.media === 'all' && link.sheet;
        link.media = 'all';
        if (ready) finish();
      });
    }
    async setupEventListener(e) {
      e.preventDefault();
      const productHandle = this.dataset.productHandle;
      if (!productHandle || this.classList.contains('loading')) return;
      clearTimeout(QuickView.closeTimer);
      QuickView.request?.controller.abort();
      QuickView.request?.trigger.classList.remove('loading');
      const productUrl = `${theme.routes.root_url}/products/${productHandle}`.replace('//', '/');
      const href = `${productUrl}?view=quick-view`;
      const controller = new AbortController();
      const sequence = QuickView.sequence = (QuickView.sequence || 0) + 1;
      QuickView.request = { controller, trigger: this };
      const cached = QuickView.cached?.url === href && Date.now() - QuickView.cached.time < 60000
        ? QuickView.cached : null;
      const timer = setTimeout(() => controller.abort(), 8000);
      this.classList.add('loading');
      try {
        const [, text] = await Promise.all([
          this.waitForStyles(controller.signal),
          cached ? Promise.resolve(cached.html)
            : fetch(href, { method: 'GET', signal: controller.signal }).then(response => {
                if (!response.ok) throw new Error('quick-view-request-failed');
                return response.text();
              })
        ]);
        if (QuickView.sequence !== sequence) return;
        const content = new DOMParser().parseFromString(text, 'text/html')
          .querySelector('#Product-Drawer-Content');
        if (!content) throw new Error('quick-view-content-missing');
        this.renderQuickview(content.innerHTML, href, productHandle);
        if (!cached) QuickView.cached = { url: href, html: text, time: Date.now() };
      } catch (error) {
        if (QuickView.sequence === sequence) window.location.assign(productUrl);
      } finally {
        clearTimeout(timer);
        controller.abort();
        if (QuickView.request?.controller === controller) {
          QuickView.request = null;
          this.classList.remove('loading');
        }
      }
    }
    renderQuickview(sectionInnerHTML, href, productHandle) {
      const sequence = QuickView.sequence;
      if (sectionInnerHTML) {

        this.drawer.querySelector('#Product-Drawer-Content').innerHTML = sectionInnerHTML;

        let js_files = this.drawer.querySelector('#Product-Drawer-Content').querySelectorAll('script');

        if (js_files.length > 0) {
          var head = document.getElementsByTagName('head')[0];
          js_files.forEach((js_file, i) => {
            let script = document.createElement('script');
            script.src = js_file.src;
            head.appendChild(script);
          });
        }

        setTimeout(() => {
          if (QuickView.sequence !== sequence || !this.drawer.classList.contains('active')) return;
          if (Shopify && Shopify.PaymentButton) {
            Shopify.PaymentButton.init();
          }
          if (window.ProductModel) {
            window.ProductModel.loadShopifyXR();
          }
        }, 300);



        this.body.classList.add('open-cc--product');
        this.drawer.classList.add('active');

        this.drawer.cbmReturnFocus = this;
        customElements.get('side-panel-close')?.syncPanelState(this.drawer);
        this.drawer.querySelector('.side-panel-close').focus();

        setTimeout(() => {
          if (QuickView.sequence !== sequence || !this.drawer.classList.contains('active')) return;
          const slider = this.drawer.querySelector('#Product-Slider');
          slider?.reInit?.();
          window.dispatchEvent(new Event('resize'));
        }, 100);

        dispatchCustomEvent('quick-view:open', {
          productUrl: href,
          productHandle: productHandle
        });
      }
    }
  }
  document.addEventListener('cart:item-added', () => { QuickView.cached = null; });
  document.addEventListener('line-item:change:end', () => { QuickView.cached = null; });
  customElements.define('quick-view', QuickView);
}

/**
 *  @class
 *  @function AnimatedMarkers
 */
class AnimatedMarkers {

  constructor() {
    this.markers = document.querySelectorAll('.svg-marker path');
    this.animations_enabled = document.body.classList.contains('animations-true') && typeof gsap !== 'undefined';

    if (this.animations_enabled && this.markers.length) {
      this.setupEventListeners();
    }
  }
  setupEventListeners() {
    this.markers.forEach((marker, i) => {
      gsap.from(marker, {
        duration: 1,
        ease: 'power4.inOut',
        drawSVG: "0%",
        scrollTrigger: {
          trigger: marker,
          start: 'top 70%',
          end: 'bottom 80%'
        }
      });
    });
  }

}
document.addEventListener('DOMContentLoaded', () => {

  if (typeof CartDrawer !== 'undefined') {
    new CartDrawer();
  }
  if (typeof AnimatedMarkers !== 'undefined') {
    new AnimatedMarkers();
  }
  if (typeof FooterMenuToggle !== 'undefined') {
    new FooterMenuToggle();
  }
});

(() => {
  let quickViewAddPending = false;
  let googleConfigured = false;
  const googleTagId = 'G-5FGWJGRQ6Z';
  const getPlacement = () => {
    const path = window.location.pathname.replace(/\/$/, '') || '/';
    const root = theme.routes.root_url.replace(/\/$/, '') || '/';
    if (path === root) return 'home';
    if (path.includes('/collections/')) return 'collection';
    return 'other';
  };
  const sendToGoogle = (name, data) => {
    const privacy = window.Shopify?.customerPrivacy;
    if (privacy?.analyticsProcessingAllowed && !privacy.analyticsProcessingAllowed()) return;
    if (typeof window.gtag !== 'function') return;
    if (!googleConfigured) {
      window.gtag('config', googleTagId, { send_page_view: false });
      googleConfigured = true;
    }
    window.gtag('event', name, {
      send_to: googleTagId,
      ...data
    });
  };
  const publish = (name, data) => {
    const payload = {
      ...data,
      placement: getPlacement()
    };
    window.Shopify?.analytics?.publish?.(name, payload);
    sendToGoogle(name, payload);
  };

  document.addEventListener('quick-view:open', (event) => {
    publish('cbm_quick_view_open', {
      product_handle: event.detail?.productHandle || ''
    });
  });
  document.addEventListener('submit', (event) => {
    quickViewAddPending = Boolean(event.target?.closest?.('#Product-Drawer'));
  }, true);
  document.addEventListener('cart:item-added', (event) => {
    if (!quickViewAddPending) return;
    quickViewAddPending = false;
    const product = event.detail?.product || {};
    publish('cbm_quick_view_add', {
      product_handle: product.handle || '',
      variant_id: String(product.variant_id || product.id || '')
    });
  });
})();




